import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import { PlanStatus } from '../../utils/constants';

// State interface for station assignment
export interface StationAssignmentState {
  selectedDate: string | null;
  selectedShift: string | null;
  planStatus: PlanStatus | null;
  planId: string | null;
  lastModifiedBy: string | null;
  lastModifiedDate: string | null;
  isStationAssignmentActive: boolean;
}

// Initial state
const initialState: StationAssignmentState = {
  selectedDate: null,
  selectedShift: null,
  planStatus: null,
  planId: null,
  lastModifiedBy: null,
  lastModifiedDate: null,
  isStationAssignmentActive: false,
};

// Create the station assignment slice
const stationAssignmentSlice = createSlice({
  name: 'stationAssignment',
  initialState,
  reducers: {
    setSelectedDate: (state, action: PayloadAction<string>) => {
      state.selectedDate = action.payload;
    },
    setSelectedShift: (state, action: PayloadAction<string>) => {
      state.selectedShift = action.payload;
    },
    setPlanStatus: (state, action: PayloadAction<PlanStatus>) => {
      state.planStatus = action.payload;
    },
    setStationAssignmentPlanId: (state, action: PayloadAction<string>) => {
      state.planId = action.payload;
    },
    setGenerationResult: (state, action: PayloadAction<{
      planId: string;
      planStatus: PlanStatus;
      lastModifiedBy?: string;
      lastModifiedDate?: string;
    }>) => {
      state.planId = action.payload.planId;
      state.planStatus = action.payload.planStatus;
      state.lastModifiedBy = action.payload.lastModifiedBy ?? null;
      state.lastModifiedDate = action.payload.lastModifiedDate ?? null;
    },
    setStationAssignmentActive: (state, action: PayloadAction<boolean>) => {
      state.isStationAssignmentActive = action.payload;
    },
    setStationAssignmentData: (state, action: PayloadAction<{ date: string; shift: string }>) => {
      state.selectedDate = action.payload.date;
      state.selectedShift = action.payload.shift;
      state.isStationAssignmentActive = true;
    },
    clearStationAssignment: (state) => {
      Object.assign(state, initialState);
    },
  },
});

// Export actions
export const {
  setSelectedDate,
  setSelectedShift,
  setPlanStatus,
  setStationAssignmentPlanId,
  setGenerationResult,
  setStationAssignmentActive,
  setStationAssignmentData,
  clearStationAssignment,
} = stationAssignmentSlice.actions;

// Export reducer
export default stationAssignmentSlice.reducer;

// Selectors
export const selectStationAssignment = createSelector(
  [(state: { stationAssignment: StationAssignmentState }) => state.stationAssignment],
  (stationAssignment) => stationAssignment
);

export const selectStationAssignmentPlanStatus = (state: { stationAssignment: StationAssignmentState }) =>
  state.stationAssignment.planStatus;

export const selectStationAssignmentPlanId = (state: { stationAssignment: StationAssignmentState }) =>
  state.stationAssignment.planId;

export const selectStationAssignmentData = createSelector(
  [
    (state: { stationAssignment: StationAssignmentState }) => state.stationAssignment.selectedDate,
    (state: { stationAssignment: StationAssignmentState }) => state.stationAssignment.selectedShift,
    (state: { stationAssignment: StationAssignmentState }) => state.stationAssignment.isStationAssignmentActive,
  ],
  (selectedDate, selectedShift, isStationAssignmentActive) => ({
    selectedDate,
    selectedShift,
    isStationAssignmentActive,
  })
);
